import { Button, addToast } from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Card, Col, Row } from 'reactstrap';

import GoBackButton from '../../../../components/buttons/GoBackButton';
import { PathsLayouts } from '../../../../config/routes';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  ActivityHour,
  getStudyPlanByIdRequest,
  ProgramUnit,
  StudyPlanListItem,
  updateProgramRequest,
} from '../api';
import { ProgramFormLoader } from '../hooks/ProgramFormLoader';
import { ProgramForm, ProgramFormValues } from '../parts/Program/ProgramForm';

export function UpdateProgram() {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [studyPlan, setStudyPlan] = useState<StudyPlanListItem>();

  const goBack = useCallback(() => {
    history.push(`${PathsLayouts.continuousEducation}/study-plans`);
  }, [history]);

  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );

  useEffect(() => {
    const fetchStudyPlan = async () => {
      const response = await getStudyPlanByIdRequest(Number(id));
      if (!response.error && response.data) {
        setStudyPlan(response.data);
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`common.msg.createError`),
          className: 'long-toast',
        });
        goBack();
      }
    };

    fetchStudyPlan();
  }, [id, t, goBack]);

  const initialValues = useMemo(() => {
    if (!studyPlan) {
      return undefined;
    }

    // Extract activity hours from the latest version
    const activityHours: { [key: string]: number } = {};
    if (
      studyPlan.studyPlanCe &&
      studyPlan.studyPlanCe.activityTypeHours &&
      studyPlan.studyPlanCe.activityTypeHours.length > 0
    ) {
      studyPlan.studyPlanCe.activityTypeHours.forEach((ah) => {
        activityHours[ah.activityTypeId] = ah.hours || 0;
      });
    }

    // Extract units and subjects from curriculum
    let units: ProgramUnit[] = [{ name: 'Unidad 1', subjects: [] }];
    let curriculumImage = null;
    if (
      studyPlan.versions &&
      studyPlan.versions.length > 0 &&
      studyPlan.versions[0].curriculums &&
      studyPlan.versions[0].curriculums.length > 0
    ) {
      const curriculum = studyPlan.versions[0].curriculums[0];
      if (curriculum.semesters && curriculum.semesters.length > 0) {
        units = curriculum.semesters
          .sort((a, b) => a.order - b.order)
          .map((semester) => ({
            id: semester.id,
            name: semester.name,
            subjects:
              semester.semesterCourses?.map((sc) => ({
                id: sc.course.id,
                name: sc.course.name,
                code: sc.course.code,
                credits: sc.course.credits,
              })) || [],
          }));
      }
      // Extract curriculum image if exists
      if (curriculum.image) {
        curriculumImage = {
          id: curriculum.image.id,
          name: curriculum.image.name,
          extension: curriculum.image.extension,
          url: curriculum.image.url,
        };
      }
    }

    return {
      name: studyPlan.name || '',
      code: studyPlan.code || '',
      modality: studyPlan.modality
        ? {
            label: studyPlan.modality.name,
            value: studyPlan.modality.id,
          }
        : null,
      school: studyPlan.school
        ? {
            label: studyPlan.school.name,
            value: studyPlan.school.id,
          }
        : null,
      totalHours: studyPlan.studyPlanCe?.hoursTotal || 0,
      activityHours,
      description: studyPlan.studyPlanCe?.description || '',
      hasCertificate: studyPlan.studyPlanCe?.hasCertificate || false,
      units,
      curriculumImage,
    };
  }, [studyPlan]);

  const onSubmit = useCallback(
    async (values: ProgramFormValues) => {
      if (!values.modality || !values.school) {
        return;
      }

      const activityHours: ActivityHour[] = Object.entries(
        values.activityHours,
      ).map(([activityTypeId, hours]) => ({
        activityTypeId: activityTypeId,
        hours: Number(hours || 0),
      }));

      const units: ProgramUnit[] = values.units.map((unit) => ({
        id: unit?.id,
        name: unit.name,
        subjects: unit.subjects.map((subject) => ({
          id: subject.id,
        })),
      }));

      const requestBody = {
        name: values.name,
        code: values.code,
        modalityId: values.modality.value as number,
        schoolId: values.school.value as number,
        description: values.description,
        hasCertificate: values.hasCertificate,
        activityHours,
        units,
        imageId: values.curriculumImage?.id,
      };

      const { error } = await updateProgramRequest(Number(id), requestBody);
      if (!error) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`common.msg.updateProgramSuccess`),
          className: 'long-toast',
        });
        goBack();
        return;
      }

      addToast({
        icon: 'error',
        color: 'danger',
        text: t(`common.msg.createError`),
        className: 'long-toast',
      });
      return error.code;
    },
    [t, goBack, id],
  );

  return (
    <Card className="p-4 mt-2 mx-3">
      <div className="mb-4">
        <GoBackButton onClick={goBack} />
      </div>
      <ProgramFormLoader fetchImmediately>
        <ProgramForm
          onSubmit={onSubmit}
          initialValues={initialValues as any}
          isEditMode={true}
          isInActiveOffer={studyPlan?.isInActiveOffer}
        >
          {({ isSubmitting }) => (
            <Row className="pt-5">
              <Col
                xs={{ order: 2, size: 12 }}
                md={{ order: 1, size: 4 }}
                className="ml-auto"
              >
                <div>
                  <Button
                    type="button"
                    text={t(`common.actions.cancel`)}
                    outlined
                    onClick={() => goBack()}
                    fullwidth
                  />
                </div>
              </Col>
              {!studyPlan?.isInActiveOffer && (
                <Col xs={{ order: 1, size: 12 }} md={{ order: 2, size: 4 }}>
                  <div className="pb-3 pb-md-0">
                    <Button
                      type="submit"
                      text={t(`common.actions.update`)}
                      loading={isSubmitting}
                      fullwidth
                    />
                  </div>
                </Col>
              )}
            </Row>
          )}
        </ProgramForm>
      </ProgramFormLoader>
    </Card>
  );
}
